import { PageSizeOptions } from '@/data/options'
import { QUERY_KEY } from '@/data/query-key'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import EditIcon from '@mui/icons-material/Edit';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import {Box, Button, IconButton, Modal, TextField, Typography} from "@mui/material";
import {useFormik} from "formik";
import {chainRules, formatEmailRule, formatPhoneRule, requiredInputStringRule} from "common/input-rules";
import {Snack, SnackbarContext} from "@/context/SnackbarContext";
import {getTutors, patchTutor, patchTutorPassword, PatchTutorPasswordData, postTutor} from "@/api/tutor";
import {CreateTutorPayload, Tutor} from "@models/tutor";
import {ConfirmModal} from "@/components/ConfirmModal";
import {patchStudentPassword, PatchStudentPasswordData} from "@/api/student";

export default function TutorList() {

    const {snack, setSnack} = useContext(SnackbarContext);

    const columns: GridColDef[] = [
        {
            field: '_id',
            headerName: '導師ID',
            sortable: false,
            flex: 1,
            minWidth: 200
        },
        {
            field: 'name',
            headerName: '名字',
            sortable: false,
            flex: 1,
            minWidth: 200
        },
        {
            field: 'email',
            headerName: '電郵',
            sortable: false,
            flex: 1,
            minWidth: 200
        },
        {
            field: 'phone',
            headerName: '電話',
            sortable: false,
            flex: 1,
            minWidth: 200
        },
        // createdAt: DbDate
        {
            field: 'action',
            headerName: '行動',
            sortable: false,
            minWidth: 120,
            renderCell: ({ row }) => {
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
                        <IconButton
                            color='primary' size='small'
                            onClick={() => clickEdit(row)}
                            sx={{ ml: 0.5 }}
                        >
                            <EditIcon></EditIcon>
                        </IconButton>
                        <IconButton
                            color='primary' size='small'
                            onClick={() => handleEditPassword(row)}
                            sx={{ ml: 0.5 }}
                        >
                            <LockPersonIcon></LockPersonIcon>
                        </IconButton>
                    </Box>
                )
            }
        }
    ]


    const [paginationModel, setPaginationModel] = useState<{page: number, pageSize: number}>({
        page: 0,
        pageSize: 50
    })
    const [keyword, setKeyword] = useState<string | null>(null)

    const query = {
        count: String(paginationModel.pageSize),
        page: String(paginationModel.page + 1),
        keyword,
    }

    //     _.omitBy({
    //
    // }, (value) => _.isNil(value) || _.isEmpty(value))

    const { data, refetch, isLoading } = useQuery(
        [QUERY_KEY.TUTOR_LIST, query],
        async () => {
            return (await getTutors({
                skip: paginationModel.pageSize * paginationModel.page,
                limit: paginationModel.pageSize,
                count: true,
                ...(!!keyword && {keyword})
            })).data
        }
    )

    const tutorList = useMemo(() => {
        if (!data)
            return []
        return data.tutors.map(u => ({
            id: u._id, // Add id for MUI
            ...u
        }))
    }, [data])

    const clickAdd = () => handleOpenModal()

    /***
     * Row actions
     */

    const clickEdit = (row: Tutor) => handleOpenModal(row)
    const handleEditPassword = (row: Tutor) => {
        passwordFormik.resetForm()
        setOpenPasswordModal(true)
        setEditingPasswordId(row._id)
    }

    /***
     * Modal
     */

    const formik = useFormik({
        initialValues: {
            phone: "",
            email: "",
            password: "",
            name: "",
        },
        onSubmit: async (values) => {
            try {
                if (editingTutor && editingTutor._id) {
                    await patchTutor({id: editingTutor._id}, {
                        phone: `+852${values.phone}`,
                        email: values.email,
                        name: values.name,
                    })
                }
                else {
                    await postTutor({
                        ...values,
                        phone: `+852${values.phone}`
                    })
                }
                setSnack(Snack.success('成功儲存'))
                handleCloseModal()
                await refetch()
            }
            catch (e) {
                setSnack(Snack.error('儲存失敗'))
            }
        },
        validateOnBlur: false,
        validateOnChange: false,
        validate: (values: CreateTutorPayload) => {
            let errors = {
                phone: chainRules([requiredInputStringRule, formatPhoneRule], values.phone),
                ...(editingTutor && !editingTutor._id && {
                    email: chainRules([requiredInputStringRule, formatEmailRule], values.email),
                    password: chainRules([requiredInputStringRule], values.password)
                })
            }
            Object.trimLeaves(errors, [true, {}]);
            return errors;
        }
    })
    const [editingTutor, setEditingTutor] = useState<Tutor | null>(null)
    const openModal = useMemo(() => {
        return editingTutor !== null
    }, [editingTutor])
    const handleOpenModal = useCallback((tutor?: Tutor) => {
        const editingTutor: Tutor = tutor || {
            _id: "",
            name: "",
            phone: "",
            email: "",
            createdAt: 0
        }
        if (tutor)
            editingTutor.phone = tutor.phone.replace("+852", "")
        formik.resetForm()
        formik.setValues(Object.assignExists({
            name: "",
            phone: "",
            email: "",
            password: "",
        }, editingTutor))
        setEditingTutor(editingTutor)
    }, [formik])
    const handleCloseModal = () => setEditingTutor(null)

    /***
     * Password Modal
     */

    const [editingPasswordId, setEditingPasswordId] = useState('')
    const [openPasswordModal, setOpenPasswordModal] = useState(false)
    const passwordFormik = useFormik({
        initialValues: {
            password: "",
        },
        onSubmit: async (values) => {
            try {
                await patchTutorPassword({id: editingPasswordId}, values)
                setSnack(Snack.success('成功儲存'))
                setOpenPasswordModal(false)
            }
            catch (e) {
                setSnack(Snack.error('儲存失敗'))
            }
        },
        validateOnBlur: false,
        validateOnChange: false,
        validate: (values: PatchTutorPasswordData) => {
            let errors = {
                password: chainRules([requiredInputStringRule], values.password)
            }
            Object.trimLeaves(errors, [true, {}]);
            return errors;
        }
    })

    return (
        <>
            <Button
                variant="contained"
                onClick={clickAdd}
            >
                新增
            </Button>
            <DataGrid
                sx={{
                    mt: 2,
                    backgroundColor: 'white'
                }}
                loading={isLoading}
                rows={tutorList}
                columns={columns}

                pagination
                paginationMode='server'
                paginationModel={paginationModel}
                rowCount={data?.count || 0}
                pageSizeOptions={PageSizeOptions}
                onPaginationModelChange={setPaginationModel}

                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableRowSelectionOnClick
            />

            {/* Details Modal */}
            <ConfirmModal title={`${editingTutor && editingTutor._id ? '修改' : '新增'}導師`}
                          open={openModal}
                          onClose={handleCloseModal}
                          confirmButtonTitle={'儲存'}
                          confirmButtonProps={{
                              disabled: !formik.dirty || formik.isSubmitting
                          }}
                          onSubmit={formik.handleSubmit}>
                <TextField sx={{mt: 2}}
                           type="email"
                           id="email"
                           label="電郵"
                           size="small"
                           fullWidth={true}
                           value={formik.values.email}
                           onChange={formik.handleChange}
                           onBlur={formik.handleBlur}
                           error={!!formik.errors.email && formik.touched.email}
                           helperText={formik.errors.email}
                />
                { editingTutor && !editingTutor._id && (
                    <TextField sx={{mt: 2}}
                               type="password"
                               id="password"
                               label="密碼"
                               size="small"
                               fullWidth={true}
                               value={formik.values.password}
                               onChange={formik.handleChange}
                               onBlur={formik.handleBlur}
                               error={!!formik.errors.password && formik.touched.password}
                               helperText={formik.errors.password}
                    />
                )}
                <TextField sx={{mt: 2}}
                           id="name"
                           label="名字"
                           size="small"
                           fullWidth={true}
                           value={formik.values.name}
                           onChange={formik.handleChange}
                           onBlur={formik.handleBlur}
                           error={!!formik.errors.name && formik.touched.name}
                           helperText={formik.errors.name}
                />
                <TextField sx={{mt: 2}}
                           inputProps={{ maxLength: 8 }}
                           id="phone"
                           label="電話"
                           size="small"
                           fullWidth={true}
                           value={formik.values.phone}
                           onChange={formik.handleChange}
                           onBlur={formik.handleBlur}
                           error={!!formik.errors.phone && formik.touched.phone}
                           helperText={formik.errors.phone}
                />
            </ConfirmModal>

            {/* Password Modal */}
            <ConfirmModal title={'修改密碼'}
                          open={openPasswordModal}
                          onClose={() => setOpenPasswordModal(false)}
                          confirmButtonTitle={'儲存'}
                          confirmButtonProps={{
                              disabled: !passwordFormik.dirty || passwordFormik.isSubmitting
                          }}
                          onSubmit={passwordFormik.handleSubmit}>
                <TextField sx={{mt: 2}}
                           type="password"
                           name="password"
                           label="密碼"
                           variant="outlined"
                           size="small"
                           fullWidth={true}
                           value={passwordFormik.values.password}
                           onChange={passwordFormik.handleChange}
                           onBlur={passwordFormik.handleBlur}
                           error={!!passwordFormik.errors.password && passwordFormik.touched.password}
                           helperText={passwordFormik.errors.password}
                />
            </ConfirmModal>
        </>
    )
}
